import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as file from 'file-saver';
import { firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { ErrorStateMatcher } from '@angular/material/core';
import * as XLSX from 'xlsx';
import { VlHttpService, VlDataService, HTTPAPIConfig, APIConfigMapping, matInputErrorStateMatcher, APP_BUILDER_PERMISSIONS, PERMISSIONS_ACTIONS } from 'shared-library';
import { WidgetPropertyFormService } from '../widgetPropertyForms/widget-property-form.service';
import { ALLOWED_INPUT_WIDGET_TYPES, WIDGET_TYPES } from '../../constants/proj.cnst';
import { EntitySubmissionsService } from './entity-submissions.service';

export interface Entity {
  application_id: string;
  child_entity_ids_list: string[];
  entity_id: string;
  entity_name: string;
  open?: boolean;
}

export interface Duration {
  label : string,
  value : string
}

@Component({
  selector: 'lib-entity-submission-details',
  templateUrl: './entity-submission-details.component.html',
  styleUrls: ['./entity-submission-details.component.scss']
})


export class EntitySubmissionDetailsComponent implements OnDestroy {
  public datePopUp!: any;
  matInputError: ErrorStateMatcher = new matInputErrorStateMatcher();
  noDataAvailable:boolean=false;
  queryParams: {
    [key: string]: string;
  } = {};
  entityList: Entity[] = [];
  durationsList: Duration[] = [
    {label: 'Last 24 hours',value: 'today'},
    {label: 'Last Week',value: 'week'},
    {label: 'Last Month',value: 'month'},
    {label: 'Custom',value: 'custom'},

  ];
  widgetTypes= WIDGET_TYPES;
  entitySubmissionDetailsForm: FormGroup = new FormGroup({
    entity_id: new FormControl('', [Validators.required]),
    duration: new FormControl(''),
  })
  entityDataFeedGroup: FormGroup = new FormGroup({
    entityId: new FormControl('', [Validators.required]),
    primaryKeys: new FormControl('', [Validators.required]),
  })
  entityEntriesViewGroup: FormGroup = new FormGroup({
    entityId: new FormControl('', [Validators.required]),
  })
  subscrptions: Subscription[] = [];
  showErrorMessage: boolean = false;
  allowedExtensions = ['csv', 'xls', 'xlsx'];
  allowedwidgetTypes:string[] = ALLOWED_INPUT_WIDGET_TYPES;
   destroy$:Subject<void> = new Subject<void>();
   fetchingWidgets:boolean = false;
   bulkUploadResponce!: any;
   bulkUploadInProcess:boolean = false;
  maxAllowedRows: number = 5000;
  resetFileInput:boolean = true;
  appPermissions = APP_BUILDER_PERMISSIONS;
  permissionsActions = PERMISSIONS_ACTIONS;

  constructor(
    private toastrService: ToastrService,
    private httpService: VlHttpService,
    private dataService: VlDataService,
    private route: ActivatedRoute,
    private widgetPropertyFormService:WidgetPropertyFormService,
    private entitySubmissionsService:EntitySubmissionsService,
  ) {
    this.subscribeRouterEvents();
    this.subscribeFormEvents();
  }

  subscribeRouterEvents(){
    const routerSubscription = this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
      this.queryParams = {};
      for (const param in params) {
        if (params.hasOwnProperty(param)) {
          this.queryParams[param] = params[param];
        }
      }
      this.initMethods();
    });
    this.subscrptions.push(routerSubscription);
  }
  
  subscribeFormEvents() {
    const changeSubscription: any = this.entitySubmissionDetailsForm.get('duration')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.noDataAvailable = false;
      if (value == 'custom') {
        this.entitySubmissionDetailsForm.addControl('fromDate', new FormControl('', [Validators.required, this.dateRangeValidator.bind(this)]));
        this.entitySubmissionDetailsForm.addControl('toDate', new FormControl('', [Validators.required , this.dateRangeValidator.bind(this)]));
      } else {
        this.entitySubmissionDetailsForm.removeControl('fromDate');
        this.entitySubmissionDetailsForm.removeControl('toDate');
      }
    })
    this.subscrptions.push(changeSubscription);
    const entityChangeSubscription: any = this.entitySubmissionDetailsForm.get('entity_id')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.noDataAvailable = false;
    })
    this.subscrptions.push(entityChangeSubscription)
  }

  dateRangeValidator(control: FormControl): { [key: string]: any } | null {
    let fromDate = this.entitySubmissionDetailsForm.get('fromDate')?.value;
    let toDate = this.entitySubmissionDetailsForm.get('toDate')?.value;
    if (fromDate && toDate && fromDate > toDate) {
      return { dateRangeError: true };
    } else {
      this.entitySubmissionDetailsForm.get('toDate')?.setErrors(null);
      this.entitySubmissionDetailsForm.get('fromDate')?.setErrors(null);
    }
    return null
  }
  
  async initMethods() {
    this.entityList = await this.fetchEntity();
  }

  async fetchEntity(entity_id: string | null = null) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_ENTITIES_OR_ATTRIBUTES']);
    apiConfig.postData = {
      application_id: this.queryParams['id'],
      entity_id: entity_id,
      project_id: null,
      parent_entity_id: null
    }
    return await new Promise<Entity[]>((resolve: any, reject) => {
      this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          resolve(response.data);
        },
        complete: () => {},
        error: (error: any) => {
          console.log(error, 'Error in Fetch entity API');
          reject(error);
        } 
      });
    });  
  }

  resetSubmissionsfield(){
    this.entitySubmissionDetailsForm.get('duration')?.reset();;
    this.noDataAvailable = false;
    this.showErrorMessage = false;
    this.datePopUp = null;
  }

  downloadEntitySubmissionData() {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_ENTITY_SUBMISSION_DATA']);
    console.error(this.entitySubmissionDetailsForm.value);
    apiConfig.postData = this.entitySubmissionDetailsForm.value
    if(!this.entitySubmissionDetailsForm.value?.duration) {
      apiConfig.postData.duration = '';
    }

    const entityName = this.entityList?.find((entity: Entity) => entity.entity_id == this.entitySubmissionDetailsForm.value?.entity_id)?.entity_name
    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        const contentType = response.type; 
        if (contentType === 'application/json') {
          this.noDataAvailable = true;
        }
        else if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          file.saveAs(blob, `Entity_Submission_data_of_${entityName}.xlsx`);
        }
      },
      complete: () => {},
      error: (err: any) => {
        this.toastrService.error('Failed to download the data.')
      }
    });
  }

  onSelectClick(): void {
    this.showErrorMessage = this.entityList.length === 0;
    if(this.showErrorMessage){
      this.entitySubmissionDetailsForm.get('entity_id')?.markAsDirty()
    }
  }

  ngOnDestroy() {
    for(const subscription of this.subscrptions) {
      subscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  datapopUpControl(field:string,calanderPopUp:any,isClosed:boolean){
    const formControl = this.entitySubmissionDetailsForm.get(field);

    if(!formControl) return;

      if(isClosed){
        formControl.setValidators(Validators.required);
        formControl.updateValueAndValidity();
      }
      else{
        formControl.removeValidators(Validators.required);
        formControl.updateValueAndValidity();      
        this.datePopUp = calanderPopUp;
        this.datePopUp.open();
      }
  }



  datapopUpControlFrom(calanderPopUp: any, isClosed: boolean) {this.datapopUpControl('fromDate', calanderPopUp, isClosed)}
  
  datapopUpControlTo(calanderPopUp: any, isClosed: boolean) { this.datapopUpControl('toDate', calanderPopUp, isClosed);}

  async onselectEntity(event:any){
    try{
      this.fetchingWidgets = true;
      const listOfPageWidgetsOfEntity:any = await firstValueFrom(this.widgetPropertyFormService.fetchEntity(event.value,this.queryParams['id']));
      this.entityWidgets=this.widgetPropertyFormService.retriveWidgetsListFromEntiywithConfig(listOfPageWidgetsOfEntity?.data?.page_widget_details)
      this.entityWidgets = this.entityWidgets.filter((widget:any) => this.allowedwidgetTypes.includes(widget.widgetType));
      if(this.fileToUpload){
        this.checkMatches();
      }
      this.entityDataFeedGroup.get('primaryKeys')?.reset();
      this.fetchingWidgets = false;
      this.bulkUploadResponce = null;
    }catch(error:any){
      console.error(error);
      this.fetchingWidgets = false;
      this.toastrService.error('Failed to get entity widgets');
    }
  }

  headers: string[] = [];
  totalRows: number = 0;
  formattedHeaders:string[] = [];
  entityWidgets:any=[];
  widgetsPayload:any = [];
  matchedWidgets = signal<any>([]);
  unmatchedWidgets = signal<any>([]);
  unmatchedHeaders = signal<any>([]);
  fileToUpload:any;
  fileName:string = '';
 
  onFileChange(event: any,inputElement:any) {
    const file = event.target.files[0];
    if(file){
      this.fileName = file.name;
      this.headers = [];
      this.processFile(file);
      this.entityDataFeedGroup.get('primaryKeys')?.reset();
      inputElement.value = '';
    }
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      const file = files[0];
      const extension = file.name.split('.').pop()?.toLowerCase();
      this.fileName = file.name;
        if (extension && this.allowedExtensions.includes(extension)) {
          this.processFile(file)
        }
    }
  }

  processFile(file:any){
    if (!file) return;
    this.bulkUploadResponce = null;
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.fileToUpload = file;
      let jsonData: any = [];

      if (fileExtension === 'csv') {
        // Handle CSV parsing
        const csvText = e.target.result;
        jsonData = this.parseCSV(csvText);
      } else {
        // Handle Excel parsing
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
      }

      if (jsonData.length < 1) {
        this.toastrService.error('File is empty.')
        this.fileToUpload = null;
        this.resetMatches();
        this.fileName = '';
      this.headers = [];
        return;
      }

      const dataRows = jsonData.slice(1).filter((row:any) => row.some((cell:any) => cell !== null && cell !== undefined &&  (typeof cell === "string" ? cell.trim() !== "" : typeof cell !== "object")));
      if (dataRows.length === 0) {
        this.toastrService.error("The file contains only headers and no data.");
        this.fileToUpload = null;
        this.resetMatches();
        this.fileName = '';
        this.headers = [];
        return;
      }

      this.headers = jsonData[0].map((header: any, index: number) => {
        if (header === undefined || header === null || String(header).trim() === "") {
          return null; 
        }
        return String(header).trim();
      }).filter((header:any) => header !== null);

      this.totalRows = jsonData.length - 1; 
      const uniqueHeaders = new Set(this.headers);
      if (uniqueHeaders.size !== this.headers.length) {
        this.toastrService.error('Duplicate column names found.');
        this.fileToUpload = null;
        this.fileName = '';
        this.headers = [];
        this.resetMatches();
        return;
      }  
      
      this.formattedHeaders = this.headers.map(header => header.toLowerCase().replace(/_/g, ' '))
      this.checkMatches();
    };
    if (fileExtension === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }

  resetMatches(){
    this.resetFileInput = false;
    this.matchedWidgets.set([]);
    this.unmatchedHeaders.set([]);
    this.unmatchedWidgets.set([]);
    this.resetFileInput = true;
  }

  generateFile(fileType:string){
    let headers = this.entityWidgets.map((widget:any) => widget.widgetLabel.toLowerCase())
    this.entitySubmissionsService.createPrefilledFile(headers,fileType)
  }


  // Helper method to parse CSV data into an array
  parseCSV(csvText: string): any[] {
    const rows = csvText.split(/\r?\n/).map(row => row.split(','));
    // Ensure no completely empty rows
    return rows;
  }

  checkMatches() {
    const matchedList: any = [];
    let unmatchedList:any = [];
    const unmatchedHeadersList: string[] = [];

    this.entityWidgets.forEach((widget:any) => {
      const formattedWidgetLabel = widget.widgetLabel;
      const index = this.headers.findIndex((header:any) => header.toLowerCase().replace(/_/g, ' ') === formattedWidgetLabel.toLowerCase());

      const widgetMatch: any = {
        widgetId: widget.widgetId,
        widgetLabel: widget.widgetLabel,
        widgetType: widget.widgetType,
        info: index != -1 ? `${widget.widgetLabel} (Matched with "${this.headers[index]}")` : undefined,
        headerKey: index != -1 ? this.headers[index] : undefined,
      };

      if (index != -1) {
        matchedList.push(widgetMatch);
      } else {
        unmatchedList.push(widgetMatch);
      }
    });

    // Find headers from the file that have no matching widget
    this.formattedHeaders.forEach(header => {
      const isMatched = this.entityWidgets.some((widget:any) =>
        widget.widgetLabel.toLowerCase().replace(/_/g, ' ') === header
      );
      if (!isMatched) {
        unmatchedHeadersList.push(header);
      }
    });

    this.matchedWidgets.set(matchedList);
    this.unmatchedWidgets.set(unmatchedList);
    this.unmatchedHeaders.set(unmatchedHeadersList);
  }


  uploadFile(){
    
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['UPLOAD_FILE']);
    apiConfig.postData = {
      file:this.fileToUpload
    }

    return this.httpService.placeAPICall(apiConfig);
  }

  async uploadBulk(){
    this.bulkUploadInProcess = true;
    const uploadedFile:any = await  firstValueFrom(this.uploadFile()); 
    const s3 = uploadedFile?.image;
    const matchedList: any = [];
    let unmatchedList:any = [];
    
    this.formattedHeaders.forEach((formattedHeader, index) => {
      const originalHeader = this.headers[index]; // Get corresponding original header
      const widget = this.entityWidgets.find((w:any) => w.widgetLabel.toLowerCase().replace(/_/g, ' ') === formattedHeader);
      
      if (widget) {
        matchedList.push({
          widgetId: widget.widgetId,
          widgetLabel: widget.widgetLabel,
          widgetType: widget.widgetType,
          headerKey: originalHeader 
        });

        unmatchedList = unmatchedList.filter((w:any) => w.widgetId !== widget.widgetId);
      }
    });
   
    let payload = {
      "csvs3url":s3,
      "entityId":this.entityDataFeedGroup.get('entityId')?.value,
      "applicationId":this.queryParams['id'],
      "widgetsMeta":matchedList,
      "primaryKeys":this.entityDataFeedGroup.get('primaryKeys')?.value
    }

    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['UPLOAD_ENTITY_DATA']);
    apiConfig.postData = payload;

    this.httpService.placeAPICall(apiConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        
        this.toastrService.success(data?.message ?? "Bulk creation and update completed successfully.");
        this.bulkUploadResponce = data;
        this.bulkUploadInProcess = false;
      },
      error: (err: any) => {
        this.bulkUploadInProcess = false;
        this.toastrService.warning("Bulk creation and update failed")
      }
    })

  }



  onDragOver(event: DragEvent) {
    event.preventDefault(); 
    event.stopPropagation();
  }
  
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }


  viewEntityEntries(){
    let entity = this.entityList.find((entity:any) => entity.entity_id === this.entityEntriesViewGroup.get('entityId')?.value);
    this.entitySubmissionsService.setEntityEntriesTable({entityId:this.entityEntriesViewGroup.get('entityId')?.value,"applicationId":this.queryParams['id'],"entityName":entity ? entity.entity_name : ''});
  }

  clearTable(event:any){
    this.entitySubmissionsService.setEntityEntriesTable({});
    this.entityEntriesViewGroup.reset();
  }

  clearUpload(event:any){
    this.entityDataFeedGroup.reset();
    this.headers = [];
    this.fileName = "";
    this.fileToUpload = "";
    this.entityWidgets = [];
    this.resetMatches();
    this.bulkUploadResponce = null;
  }


}
